import { track } from "@helsenorge/framework-utils/amplitude";
import {
  ApnetOppgave,
  StartetOppgave,
  type ApnetOppgaveProperties,
} from "@helsenorge/framework-utils/amplitude/ampli";
import { differenceInCalendarDays, parseISO, startOfDay } from "date-fns";
import { ElementType } from "enums/elementType";
import { Intensjon } from "enums/intensjon";
import { OppgaveStatus } from "enums/oppgaveStatus";
import { OppgaveType } from "enums/oppgaveType";
import type { Oppgave } from "HNEntities/oppgave";

const parseFristDato = (frist: Date | string): Date =>
  typeof frist === "string" ? parseISO(frist) : frist;

const getOppgaveFristStatus = (
  frist: Date | string | undefined,
): "utløpt" | "ikke utløpt" => {
  if (!frist) {
    return "ikke utløpt";
  }

  const parsedFrist = parseFristDato(frist);
  if (isNaN(parsedFrist.getTime())) {
    return "ikke utløpt";
  }

  return startOfDay(parsedFrist) < startOfDay(new Date())
    ? "utløpt"
    : "ikke utløpt";
};

const getOppgaveFristDager = (
  frist: Date | string | undefined,
  status: OppgaveStatus | undefined,
): number | undefined => {
  if (
    status === OppgaveStatus.Cancelled ||
    status === OppgaveStatus.EnteredInError ||
    status === OppgaveStatus.Expired
  ) {
    return undefined;
  }

  if (!frist) {
    return undefined;
  }

  const parsedFrist = parseFristDato(frist);
  if (isNaN(parsedFrist.getTime())) {
    return undefined;
  }

  const dagerTilFrist = differenceInCalendarDays(
    startOfDay(parsedFrist),
    startOfDay(new Date()),
  );

  return Math.max(0, dagerTilFrist);
};

const getOppgaveStatus = (
  status: OppgaveStatus | undefined,
): ApnetOppgaveProperties["oppgavestatus"] => {
  switch (status) {
    case OppgaveStatus.Ready:
      return "aktiv";
    case OppgaveStatus.InProgress:
      return "påbegynt";
    case OppgaveStatus.Completed:
      return "utført";
    case OppgaveStatus.Cancelled:
    case OppgaveStatus.EnteredInError:
    case OppgaveStatus.Expired:
      return "kansellert";
    default:
      return undefined;
  }
};

const getOppgaveType = (
  type: OppgaveType | undefined,
): ApnetOppgaveProperties["oppgaveType"] => {
  switch (type) {
    case OppgaveType.Skjema:
      return "skjema";
    case OppgaveType.Samtykke:
      return "samtykke";
    case OppgaveType.Betaling:
      return "betaling";
    case OppgaveType.Informasjon:
      return "informasjon";
    case OppgaveType.Verktoy:
      return "verktøy";
    case OppgaveType.Ekstern:
      return "ekstern oppgave";
    default:
      return undefined;
  }
};

const getObligatoriskOppgave = (
  intensjon: Intensjon | undefined,
): boolean | undefined => {
  if (intensjon === undefined) {
    return undefined;
  }

  return intensjon === Intensjon.Plan;
};

const getOppgaveTilknytning = (
  oppgave: Oppgave,
): ApnetOppgaveProperties["oppgaveTilknytning"] => {
  if (!oppgave.Referanser?.length) {
    return undefined;
  }

  if (
    oppgave.Referanser.some(
      (referanse) => referanse.Type === ElementType.Timeavtale,
    )
  ) {
    return "timeavtale";
  }

  if (
    oppgave.Referanser.some(
      (referanse) => referanse.Type === ElementType.Henvisning,
    )
  ) {
    return "henvisning";
  }

  return undefined;
};

export const sporAapningAvOppgave = (oppgave: Oppgave) => {
  track(
    new ApnetOppgave({
      handling: "se",
      objekt: "oppgave",
      oppgavestatus: getOppgaveStatus(oppgave?.Status),
      oppgaveType: getOppgaveType(oppgave?.Type),
      obligatoriskOppgave: getObligatoriskOppgave(oppgave?.Intensjon),
      oppgaveTilknytning: getOppgaveTilknytning(oppgave),
      oppgavefristDager: getOppgaveFristDager(oppgave?.Frist, oppgave?.Status),
      oppgavefristStatus: getOppgaveFristStatus(oppgave?.Frist),
    }),
  );
};

export const sporStartOppgave = (oppgave: Oppgave) => {
  track(
    new StartetOppgave({
      handling: "utføre",
      objekt: "oppgave",
      oppgavestatus: getOppgaveStatus(oppgave?.Status),
      oppgaveType: getOppgaveType(oppgave?.Type),
      obligatoriskOppgave: getObligatoriskOppgave(oppgave?.Intensjon),
      oppgaveTilknytning: getOppgaveTilknytning(oppgave),
      oppgavefristDager: getOppgaveFristDager(oppgave?.Frist, oppgave?.Status),
      oppgavefristStatus: getOppgaveFristStatus(oppgave?.Frist),
    }),
  );
};
